import Joi from 'joi'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import SelectField from '../../../../UI/SelectField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { schemaOptions } from '../../../../shared/i18n'
import { askConfirm } from '../../../../shared/confirmDialog'
import { types } from '../../../../shared/eventEmitter'

import styles from '../mergeDialog.module.scss'

const schema = Joi.object({
	school: Joi.object({
		id: Joi.number().integer().required()
	})
		.unknown()
		.required()
})

// "An der Traun " + "1" + "4650" + "Lambach" — die Anschrift steht in vier
// Feldern, im Bestand oft mit angehaengtem Leerzeichen.
const addressOf = school =>
	[[school.streetName, school.number].filter(Boolean).join(' '), [school.zipCode, school.location].join(' ')]
		.map(part => part.trim())
		.filter(Boolean)
		.join(', ')

function SchoolCard(props) {
	const { t } = useTranslation()
	const { school } = props

	return (
		<div className={clsx(styles.card, props.tone === 'from' ? styles.cardFrom : styles.cardTo)}>
			<div className={styles.cardHead}>
				<span className={styles.cardName}>{school.name}</span>
				<span className={clsx(styles.badge, school.verified ? styles.badgeVerified : styles.badgeOpen)}>
					{school.verified ? t('Verified') : t('Not Verified')}
				</span>
			</div>
			<p className={styles.cardLine}>{addressOf(school)}</p>
			{school.type && <p className={styles.cardLine}>{school.type}</p>}
			{props.note && <p className={styles.cardLine}>{props.note}</p>}
		</div>
	)
}

SchoolCard.propTypes = {
	school: PropTypes.object.isRequired,
	tone: PropTypes.oneOf(['from', 'to']).isRequired,
	note: PropTypes.string
}

export default function MergeSchoolsModal() {
	const { t } = useTranslation()
	const {
		reset,
		watch,
		control,
		handleSubmit,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)
	const [fromSchool, setFromSchool] = useState({})
	const [teacherCount, setTeacherCount] = useState(null)

	const toSchool = watch('school')

	/*
	 * Wie viele Lehrkraefte haengen an der Schule, die aufgeloest wird? Genau die
	 * werden umgehaengt — ohne die Zahl bleibt im Dunkeln, wie gross der Eingriff
	 * ist. Scheitert die Abfrage, bleibt die Zeile weg statt eine Fehlermeldung
	 * ueber den Dialog zu legen.
	 */
	const loadTeacherCount = schoolId => {
		setTeacherCount(null)
		http.get(TApi.ADMIN_GET_TEACHERS, {
			params: { schoolId, limit: 1, offset: 0 },
			disableToast: true
		})
			.then(({ data }) => setTeacherCount(data.count))
			.catch(() => setTeacherCount(null))
	}

	const handleOpen = (fromSchool, onDone) => {
		setFromSchool(fromSchool)
		setOnDone(() => onDone)
		reset({ school: null })
		if (fromSchool?.id) loadTeacherCount(fromSchool.id)
	}

	const teacherNote = () => {
		if (teacherCount === null) return undefined
		if (teacherCount === 0) return t('No teachers are assigned to this school.')
		if (teacherCount === 1) return t('One teacher moves to the remaining school.')

		return t('teachersMoveNote', { count: teacherCount })
	}

	const loadSchoolOptions = async (search, loadedOptions) => {
		const {
			data: { rows, count }
		} = await http.get(TApi.ADMIN_GET_SCHOOLS, {
			params: {
				search,
				limit: 30,
				except: fromSchool.id,
				offset: loadedOptions.length
			}
		})

		return {
			options: rows.map(row => ({ ...row, value: row.id, label: row.name })),
			hasMore: loadedOptions.length < count
		}
	}

	const onSubmit = async (fields, closeModal) => {
		// Der Vorgang loescht eine Schule und laesst sich nicht zuruecknehmen —
		// deshalb noch einmal im Klartext nachfragen, mit beiden Namen darin.
		const confirmed = await askConfirm({
			title: t('Merge schools?'),
			text: t('mergeSchoolsConfirmText', { from: fromSchool.name, to: fields.school.name }),
			confirmLabel: t('Merge schools'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)

		http.put(TApi.ADMIN_PUT_SCHOOLS_MERGE, {
			fromSchoolId: fromSchool.id,
			toSchoolId: fields.school.id
		})
			.then(() => {
				toast.success(t('mergeSchoolsDone', { to: fields.school.name }))
				closeModal()
				reset({ school: null })
				if (onDone) onDone()
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<BaseModal
			isLoading={isLoading}
			onOpen={handleOpen}
			nameEvent={types.openMergeSchoolModal}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('Merge schools')}
			maxWidth={560}
		>
			{({ closeModal, requestClose }) => (
				<form className={styles.root} onSubmit={handleSubmit(form => onSubmit(form, closeModal))}>
					<p className={styles.intro}>{t('mergeSchoolsIntro')}</p>

					<span className={styles.stepLabel}>{t('This school is dissolved')}</span>
					<SchoolCard school={fromSchool} tone="from" note={teacherNote()} />

					<div className={styles.arrow}>
						<Icon className={styles.arrowIcon} name="mergeArrow" size={{ w: 24, h: 28 }} color="#22404D" />
						<span>{t('Teachers, companies and students move along')}</span>
					</div>

					<SelectField
						async
						required
						label={t('This school remains')}
						isSearchable
						// Der Text kommt nicht von Joi: Dessen deutsche Meldungen kennen
						// den Fall "kein Eintrag gewaehlt" nicht und fielen auf
						// '"school" must be of type object' zurueck.
						error={errors.school && { message: t('Please choose the school that remains') }}
						disabled={isLoading}
						control={control}
						name="school"
						className={styles.selectField}
						placeholder={t('Search school by name, place or postal code')}
						debounceTimeout={400}
						loadOptions={loadSchoolOptions}
						// Im aufgeklappten Verzeichnis steht die Anschrift dazu — bei
						// gleichnamigen Schulen ist sie das Unterscheidungsmerkmal. Im
						// Feld selbst reicht der Name; die Einzelheiten stehen darunter
						// in der Karte.
						formatOptionLabel={({ label, ...row }, { context }) =>
							context === 'value' ? (
								<span className={styles.cardName}>{row.name}</span>
							) : (
								<div className={styles.option}>
									<div className={styles.optionHead}>
										<span className={styles.cardName}>{row.name}</span>
										<span
											className={clsx(
												styles.badge,
												row.verified ? styles.badgeVerified : styles.badgeOpen
											)}
										>
											{row.verified ? t('Verified') : t('Not Verified')}
										</span>
									</div>
									<span className={styles.optionLine}>{addressOf(row)}</span>
								</div>
							)
						}
					/>

					{toSchool?.id && <SchoolCard school={toSchool} tone="to" />}

					<div className={styles.warning}>
						<Icon className={styles.warningIcon} name="danger" size={{ w: 18, h: 18 }} color="#EF8400" />
						<span>{t('mergeSchoolsWarning')}</span>
					</div>

					<div className={styles.actions}>
						<Btn type="button" theme="secondary" disabled={isLoading} onClick={requestClose}>
							{t('Cancel')}
						</Btn>
						<Btn loading={isLoading} disabled={isLoading} type="submit">
							{t('Merge schools')}
						</Btn>
					</div>
				</form>
			)}
		</BaseModal>
	)
}
